import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { decide, TIERS, type PracticeSummary, type Tier } from '@emr/shared';

import {
  fetchPlatformAuditLog,
  fetchPlatformOperators,
  fetchPractices,
  reactivatePractice,
  revokePlatformOperator,
  suspendPractice,
  updatePracticeTier,
} from '../api/platform.ts';
import { ApiError } from '../api/client.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { AsyncSection } from '../components/AsyncState.tsx';
import { Button, Panel } from '../components/primitives.tsx';
import { useHasRoomForTable } from '../hooks/useMediaQuery.ts';

/**
 * The platform-operations console (`BACKLOG.md` M4): practice browse/suspend/reactivate/tier-
 * change, platform-operator management, and the platform's own audit log.
 *
 * **Gated on `platform:manage_practices`/`platform:read_audit_log`, never an org capability.**
 * `GatedPages.tsx`'s `GatedRoute` pattern already composes correctly here with no extension needed:
 * `useGrants()` already surfaces `isPlatformOperator`, and `decide()` already branches
 * `PLATFORM_CAPABILITIES` on that field regardless of which org (if any) the session happens to be
 * scoped to — the same chokepoint, the same function, one more capability value. What would be a
 * genuinely new problem — a route that needs to show data for *no particular org* — is handled by
 * this page never reading `useSession().orgId` at all; every query below is platform-wide by
 * construction, not org-scoped-then-widened.
 *
 * Every practice-list PHI concern that governs `AccessPage.tsx`'s table/card split applies again
 * here for the same reason: a practice name is user-submitted, unbounded text, so it gets the same
 * wrap discipline (`claude-kit/rules/invariants.md`, "Table columns need a deliberate strategy for
 * long or variable-length content") rather than trusting it to stay short.
 */

function PlatformRefusal() {
  const { t } = useTranslation('platform');
  return (
    <Panel title={t('refusal.title')}>
      <p className="m-0 text-sm text-ink">{t('refusal.body')}</p>
    </Panel>
  );
}

export function PlatformPage() {
  const { t } = useTranslation('platform');
  const grants = useGrants();
  const canManage = decide(grants, 'platform:manage_practices');
  const canReadAuditLog = decide(grants, 'platform:read_audit_log');

  if (!canManage && !canReadAuditLog) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>
        <PlatformRefusal />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>
      {canManage ? <PracticesSection /> : null}
      {canManage ? <OperatorsSection /> : null}
      {canReadAuditLog ? <AuditLogSection /> : null}
    </div>
  );
}

// `AsyncSection` itself now lives in `components/AsyncState.tsx` — extracted from here once
// `routes/PracticeBrandingPage.tsx` needed the identical shape a second time. See that file's own
// header comment for the reasoning.

// --- Practices ------------------------------------------------------------------------------------

const PRACTICES_QUERY_KEY = ['platform', 'practices'] as const;

function PracticesSection() {
  const { t } = useTranslation('platform');
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: PRACTICES_QUERY_KEY,
    queryFn: ({ signal }) => fetchPractices(signal),
  });
  // 40, matching `PracticesTable`'s own declared `min-width: 40rem` — a wider table than
  // `AccessPage.tsx`'s (`useHasRoomForTable`'s 34rem default), so it needs its own breakpoint rather
  // than the shared default. See that hook's own comment for the bug this fixes.
  const hasRoomForTable = useHasRoomForTable(40);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: PRACTICES_QUERY_KEY });
  // Shared by all three mutations below rather than repeated per one — each only differs in
  // `mutationFn`, and a divergent copy of this closure is exactly the kind of drift `claude-kit/
  // rules/invariants.md`'s "favor the general, reusable solution once a pattern actually recurs"
  // warns against once a third near-identical copy is about to be written.
  const onMutationError = (error: unknown) => {
    setMutationError(describeError(error, t));
  };

  const suspend = useMutation({
    mutationFn: suspendPractice,
    onSuccess: invalidate,
    onError: onMutationError,
  });
  const reactivate = useMutation({
    mutationFn: reactivatePractice,
    onSuccess: invalidate,
    onError: onMutationError,
  });
  const changeTier = useMutation({
    mutationFn: (params: { orgId: string; tier: Tier }) =>
      updatePracticeTier(params.orgId, params.tier),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  return (
    <section className="flex flex-col gap-3">
      <h2 className="m-0 text-lg font-semibold text-ink">{t('practices.heading')}</h2>
      <Panel>
        <AsyncSection
          query={query}
          loadingLabel={t('practices.loading')}
          isEmpty={(data) => data.practices.length === 0}
          emptyLabel={t('practices.empty')}
        >
          {(data) => (
            <>
              {mutationError !== null ? (
                <p role="alert" className="m-0 mb-3 text-sm font-medium text-danger">
                  {mutationError}
                </p>
              ) : null}
              {hasRoomForTable ? (
                <PracticesTable
                  practices={data.practices}
                  onSuspend={(orgId) => {
                    setMutationError(null);
                    suspend.mutate(orgId);
                  }}
                  onReactivate={(orgId) => {
                    setMutationError(null);
                    reactivate.mutate(orgId);
                  }}
                  onChangeTier={(orgId, tier) => {
                    setMutationError(null);
                    changeTier.mutate({ orgId, tier });
                  }}
                  pending={suspend.isPending || reactivate.isPending || changeTier.isPending}
                />
              ) : (
                <PracticesCards
                  practices={data.practices}
                  onSuspend={(orgId) => {
                    setMutationError(null);
                    suspend.mutate(orgId);
                  }}
                  onReactivate={(orgId) => {
                    setMutationError(null);
                    reactivate.mutate(orgId);
                  }}
                  onChangeTier={(orgId, tier) => {
                    setMutationError(null);
                    changeTier.mutate({ orgId, tier });
                  }}
                  pending={suspend.isPending || reactivate.isPending || changeTier.isPending}
                />
              )}
            </>
          )}
        </AsyncSection>
      </Panel>
    </section>
  );
}

/** Takes `t` as a parameter — a plain function, not a component or hook, so it cannot call
 * `useTranslation` itself (same pattern as `LoginPage.tsx`'s `loginErrorMessage`). */
function describeError(error: unknown, t: TFunction<'platform'>): string {
  if (error instanceof ApiError) {
    if (error.status === 503 && error.code === 'platform_ops_unavailable') {
      return t('errors.platformOpsUnavailable');
    }
    if (error.status === 404) return t('errors.practiceNotFound');
    return error.message;
  }
  return t('errors.generic');
}

function TierSelect({
  value,
  disabled,
  onChange,
}: {
  readonly value: Tier;
  readonly disabled: boolean;
  readonly onChange: (tier: Tier) => void;
}) {
  const { t } = useTranslation('platform');
  return (
    <select
      aria-label={t('practices.planTierAriaLabel')}
      value={value}
      disabled={disabled}
      onChange={(event) => {
        onChange(event.target.value as Tier);
      }}
      className="rounded-control border border-field-line bg-field px-2 py-1.5 text-sm text-ink disabled:cursor-not-allowed disabled:opacity-60"
    >
      {TIERS.map((tier) => (
        <option key={tier} value={tier}>
          {tier}
        </option>
      ))}
    </select>
  );
}

function SuspensionActions({
  practice,
  pending,
  onSuspend,
  onReactivate,
}: {
  readonly practice: PracticeSummary;
  readonly pending: boolean;
  readonly onSuspend: () => void;
  readonly onReactivate: () => void;
}) {
  const { t } = useTranslation('platform');
  return practice.suspendedAt === null ? (
    <Button variant="secondary" pending={pending} onClick={onSuspend}>
      {t('practices.suspend')}
    </Button>
  ) : (
    <Button variant="primary" pending={pending} onClick={onReactivate}>
      {t('practices.reactivate')}
    </Button>
  );
}

interface PracticesListProps {
  readonly practices: readonly PracticeSummary[];
  readonly pending: boolean;
  readonly onSuspend: (orgId: string) => void;
  readonly onReactivate: (orgId: string) => void;
  readonly onChangeTier: (orgId: string, tier: Tier) => void;
}

/**
 * The wide layout. `min-width: 40rem` matches this table's own five columns needing more room than
 * `AccessPage.tsx`'s three-column table — `PracticesSection` passes that same `40` into
 * `useHasRoomForTable` so the breakpoint tracks this declared width exactly
 * (`docs/comparison-emrx.md` §7's "derive a breakpoint from the table's own declared min-width"),
 * rather than reusing `AccessPage.tsx`'s narrower 34rem default and mounting a table too wide for
 * the viewport it just claimed to have room for.
 */
function PracticesTable({
  practices,
  pending,
  onSuspend,
  onReactivate,
  onChangeTier,
}: PracticesListProps) {
  const { t } = useTranslation('platform');
  return (
    <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
      <caption className="sr-only">{t('practices.tableCaption')}</caption>
      <thead>
        <tr className="bg-table-head">
          <th scope="col" className="rounded-l-control px-3 py-2 font-semibold text-ink">
            {t('practices.columns.practice')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('practices.columns.tier')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('practices.columns.members')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('practices.columns.status')}
          </th>
          <th scope="col" className="rounded-r-control px-3 py-2 font-semibold text-ink">
            {t('practices.columns.action')}
          </th>
        </tr>
      </thead>
      <tbody>
        {practices.map((practice) => (
          <tr key={practice.id} className="border-b border-row-line last:border-b-0">
            {/* Practice name is user-submitted at signup and genuinely unbounded — wrapped, never
                nowrap, per the workspace's table-column invariant. */}
            <th
              scope="row"
              className="max-w-[24ch] px-3 py-3 align-top font-normal break-words whitespace-normal text-ink"
            >
              {practice.name}
            </th>
            <td className="px-3 py-3 align-top">
              <TierSelect
                value={practice.tier}
                disabled={pending}
                onChange={(tier) => {
                  onChangeTier(practice.id, tier);
                }}
              />
            </td>
            <td className="px-3 py-3 align-top whitespace-nowrap text-ink">
              {practice.memberCount}
            </td>
            <td className="px-3 py-3 align-top whitespace-nowrap">
              {practice.suspendedAt === null ? (
                <span className="text-success">{t('practices.active')}</span>
              ) : (
                <span className="font-medium text-danger">{t('practices.suspended')}</span>
              )}
            </td>
            <td className="px-3 py-3 align-top whitespace-nowrap">
              <SuspensionActions
                practice={practice}
                pending={pending}
                onSuspend={() => {
                  onSuspend(practice.id);
                }}
                onReactivate={() => {
                  onReactivate(practice.id);
                }}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The narrow layout: separate markup, matching `AccessPage.tsx`'s own reasoning for why a table
 * that scrolls sideways is not an acceptable substitute below the breakpoint. */
function PracticesCards({
  practices,
  pending,
  onSuspend,
  onReactivate,
  onChangeTier,
}: PracticesListProps) {
  const { t } = useTranslation('platform');
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {practices.map((practice) => (
        <li key={practice.id} className="rounded-control border border-line p-3 text-sm">
          <p className="m-0 font-medium break-words whitespace-normal text-ink">{practice.name}</p>
          <p className="m-0 mt-1 text-ink-muted">
            {t('practices.memberCount', { count: practice.memberCount })} ·{' '}
            {practice.suspendedAt === null ? (
              <span className="text-success">{t('practices.active')}</span>
            ) : (
              <span className="font-medium text-danger">{t('practices.suspended')}</span>
            )}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <TierSelect
              value={practice.tier}
              disabled={pending}
              onChange={(tier) => {
                onChangeTier(practice.id, tier);
              }}
            />
            <SuspensionActions
              practice={practice}
              pending={pending}
              onSuspend={() => {
                onSuspend(practice.id);
              }}
              onReactivate={() => {
                onReactivate(practice.id);
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

// --- Platform operators ---------------------------------------------------------------------------

const OPERATORS_QUERY_KEY = ['platform', 'operators'] as const;

function OperatorsSection() {
  const { t } = useTranslation('platform');
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: OPERATORS_QUERY_KEY,
    queryFn: ({ signal }) => fetchPlatformOperators(signal),
  });
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: revokePlatformOperator,
    onSuccess: () => {
      setRevokeError(null);
      void queryClient.invalidateQueries({ queryKey: OPERATORS_QUERY_KEY });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'last_platform_operator') {
        setRevokeError(t('errors.lastPlatformOperator'));
        return;
      }
      setRevokeError(describeError(error, t));
    },
  });

  return (
    <section className="flex flex-col gap-3">
      <h2 className="m-0 text-lg font-semibold text-ink">{t('operators.heading')}</h2>
      <Panel>
        <p className="m-0 mb-3 text-sm text-ink-muted">
          <Trans
            t={t}
            i18nKey="operators.bootstrapIntro"
            components={{ code: <code className="font-mono" /> }}
          />
        </p>
        <AsyncSection query={query} loadingLabel={t('operators.loading')}>
          {(data) => (
            <>
              {revokeError !== null ? (
                <p role="alert" className="m-0 mb-3 text-sm font-medium text-danger">
                  {revokeError}
                </p>
              ) : null}
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {data.operators.map((operator) => (
                  <li
                    key={operator.userId}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-row-line py-2 last:border-b-0"
                  >
                    <span className="min-w-0 break-words text-ink">{operator.email}</span>
                    <Button
                      variant="secondary"
                      pending={revoke.isPending && revoke.variables === operator.userId}
                      onClick={() => {
                        revoke.mutate(operator.userId);
                      }}
                    >
                      {t('operators.revoke')}
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </AsyncSection>
      </Panel>
    </section>
  );
}

// --- Platform audit log ----------------------------------------------------------------------------

const AUDIT_LOG_QUERY_KEY = ['platform', 'audit-log'] as const;

function AuditLogSection() {
  const { t } = useTranslation('platform');
  const query = useQuery({
    queryKey: AUDIT_LOG_QUERY_KEY,
    queryFn: ({ signal }) => fetchPlatformAuditLog(signal),
  });
  // 44, matching `AuditLogTable`'s own declared `min-width: 44rem` — five columns including two
  // monospace id cells, wider still than the practices table. See `useHasRoomForTable`'s own comment
  // for why each table needs its own breakpoint rather than sharing one.
  const hasRoomForTable = useHasRoomForTable(44);

  return (
    <section className="flex flex-col gap-3">
      <h2 className="m-0 text-lg font-semibold text-ink">{t('auditLog.heading')}</h2>
      <Panel>
        <p className="m-0 mb-3 text-sm text-ink-muted">{t('auditLog.intro')}</p>
        <AsyncSection
          query={query}
          loadingLabel={t('auditLog.loading')}
          isEmpty={(data) => data.events.length === 0}
          emptyLabel={t('auditLog.empty')}
        >
          {(data) =>
            hasRoomForTable ? (
              <AuditLogTable events={data.events} />
            ) : (
              <AuditLogCards events={data.events} />
            )
          }
        </AsyncSection>
      </Panel>
    </section>
  );
}

type AuditLogEvents = Awaited<ReturnType<typeof fetchPlatformAuditLog>>['events'];

/** `resourceId`/`actorUserId` are UUIDs — no natural break point at all, so they get `break-all`
 * rather than `break-words`: `claude-kit/rules/invariants.md`'s own distinction between a short
 * field with no break points (a hash, an id) and ordinary prose. */
function AuditLogTable({ events }: { readonly events: AuditLogEvents }) {
  const { t } = useTranslation('platform');
  return (
    <table className="w-full min-w-[44rem] border-collapse text-left text-sm">
      <caption className="sr-only">{t('auditLog.tableCaption')}</caption>
      <thead>
        <tr className="bg-table-head">
          <th scope="col" className="rounded-l-control px-3 py-2 font-semibold text-ink">
            {t('auditLog.columns.when')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('auditLog.columns.actor')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('auditLog.columns.action')}
          </th>
          <th scope="col" className="px-3 py-2 font-semibold text-ink">
            {t('auditLog.columns.resource')}
          </th>
          <th scope="col" className="rounded-r-control px-3 py-2 font-semibold text-ink">
            {t('auditLog.columns.outcome')}
          </th>
        </tr>
      </thead>
      <tbody>
        {events.map((event, index) => (
          // No single-column natural key: two events can share every displayed field within the
          // same millisecond. Composite of every displayed value plus position is stable for a
          // read-only list that never reorders under the user.
          <tr
            key={`${event.occurredAt}-${event.actorUserId}-${event.action}-${event.resourceId ?? 'none'}-${String(index)}`}
            className="border-b border-row-line last:border-b-0"
          >
            <td className="px-3 py-3 align-top whitespace-nowrap text-ink-muted">
              {event.occurredAt}
            </td>
            <td className="max-w-[18ch] px-3 py-3 align-top font-mono break-all text-ink">
              {event.actorUserId}
            </td>
            <td className="px-3 py-3 align-top whitespace-nowrap text-ink">{event.action}</td>
            <td className="max-w-[22ch] px-3 py-3 align-top break-all text-ink">
              {event.resourceType}
              {event.resourceId !== null ? (
                <span className="block font-mono text-xs text-ink-muted">{event.resourceId}</span>
              ) : null}
            </td>
            <td className="px-3 py-3 align-top whitespace-nowrap">
              {event.outcome === 'allowed' ? (
                <span className="text-success">{t('auditLog.allowed')}</span>
              ) : (
                <span className="font-medium text-danger">{t('auditLog.denied')}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AuditLogCards({ events }: { readonly events: AuditLogEvents }) {
  const { t } = useTranslation('platform');
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {events.map((event, index) => (
        <li
          key={`${event.occurredAt}-${event.actorUserId}-${event.action}-${event.resourceId ?? 'none'}-${String(index)}`}
          className="rounded-control border border-line p-3 text-sm"
        >
          <p className="m-0 text-ink-muted">{event.occurredAt}</p>
          <p className="m-0 mt-1 break-words text-ink">
            {event.action} {event.resourceType}
            {event.resourceId !== null ? (
              <span className="block font-mono text-xs break-all text-ink-muted">
                {event.resourceId}
              </span>
            ) : null}
          </p>
          <p className="m-0 mt-1 break-all font-mono text-xs text-ink-muted">{event.actorUserId}</p>
          <p className="m-0 mt-1">
            {event.outcome === 'allowed' ? (
              <span className="text-success">{t('auditLog.allowed')}</span>
            ) : (
              <span className="font-medium text-danger">{t('auditLog.denied')}</span>
            )}
          </p>
        </li>
      ))}
    </ul>
  );
}
